import { RestLink } from 'apollo-link-rest';
import {
  NormalizedCacheObject, ApolloClient, InMemoryCache, ApolloLink,
} from '@apollo/client';
import { proxy, useProxy } from 'valtio';
import { useCallback, useEffect, useState } from 'react';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getConfig } from '@src/config';
import { useHistory } from 'react-router-dom';
import { getSnackBarService } from '@hooks/useSnackBar';
import useIpAddress from './useIpAddress';
import { oidcUserStore, loadUser, getAccessToken, signout } from '@libs/oidc-client';
import { subscribeKey } from 'valtio/utils';

const PLEASE_AUTHENTICATE_ERROR_MESSAGE = 'please authenticate';
const X_FV_APP_HEADER_VALUE = 'timely';

export type FilevineApolloClient = ApolloClient<NormalizedCacheObject>;

interface IFilevineClientHook {
  client: FilevineApolloClient;
  logout: VoidFunction;
  onFVUserConnectSuccess: VoidFunction;
  changeOrg: (orgId: number) => void;
  isOidcAuthenticated: boolean;
}

enum LocalStorageKey {
  AccessToken = 'accessToken',
  OrgId = 'orgId',
  UserId = 'userId',
  UserOrgs = 'userOrgs',
  TenantID = 'tenantId',
  TenantUrl = 'tenantUrl',
}

interface IUserId {
  userId: {
    native: number;
    partnerId: string;
  }
}

export interface IUserOrg {
  name: string;
  orgId: number;
}

interface IUserAndOrgId {
  user: IUserId;
  orgs: IUserOrg[];
}

interface IFilevineState {
  userId?: number;
  orgId?: number;
  userOrgs: IUserOrg[];
  client?: FilevineApolloClient;
  headers?: any;
  tenantId?: string;
  tenantUrl?: string;
}

interface IFVAuthTenantConfig {
  tenantID?: string;
  name?: string;
  friendlyKey?: string;
  shardID?: string;
  isShardTenant?: boolean;
  salesforceID?: string;
  hostNames?: ITenantHostName[];
}

export interface ITenantHostName {
  tenantHostNameID: string;
  hostNameType: number;
  hostName: string;
}

export interface ITenantConfig {
  tenantID?: string;
  tenantUrl?: string;
  error?: string;
}

const userId = localStorage.getItem(LocalStorageKey.UserId);
const orgId = localStorage.getItem(LocalStorageKey.OrgId);
const userOrgs = localStorage.getItem(LocalStorageKey.UserOrgs);
const tenantId = localStorage.getItem(LocalStorageKey.TenantID);
const tenantUrl = localStorage.getItem(LocalStorageKey.TenantUrl);

export const filevineState = proxy<IFilevineState>({
  userId: userId ? +userId : undefined,
  orgId: orgId ? +orgId : undefined,
  userOrgs: userOrgs ? JSON.parse(userOrgs) : [],
  tenantId: tenantId ? tenantId : undefined,
  tenantUrl: tenantUrl ? tenantUrl : undefined,
});

subscribeKey(filevineState, 'orgId', (value?: any) => localStorage.setItem(LocalStorageKey.OrgId, value?.toString() || ''));
subscribeKey(filevineState, 'userId', (value?: any) => localStorage.setItem(LocalStorageKey.UserId, value?.toString() || ''));
subscribeKey(filevineState, 'userOrgs', (value?: any) => localStorage.setItem(LocalStorageKey.UserOrgs, JSON.stringify(value)));
subscribeKey(filevineState, 'tenantId', (value?: any) => localStorage.setItem(LocalStorageKey.TenantID, value?.toString() || ''));
subscribeKey(filevineState, 'tenantUrl', (value?: any) => localStorage.setItem(LocalStorageKey.TenantUrl, value?.toString() || ''));

const showLoginPage = () => {
  if (!window.location.href.includes('login') && !window.location.href.includes('success')) {
    window.location.assign('/login');
  }
};

const getErrorLink = (logoutFVID: () => void) => onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      // eslint-disable-next-line no-console
      console.error(`[Query error] Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}, Operation: ${operation.operationName}`);
    });
  }

  if (networkError) {
    // eslint-disable-next-line no-console
    console.error(`[Network error] Operation: ${operation.operationName} Network Error: ${networkError}`);
    const errorMessage = networkError?.message?.toLocaleLowerCase();
    if (errorMessage?.includes('status code 401')) {
      logoutFVID();
    } else if (errorMessage?.includes(PLEASE_AUTHENTICATE_ERROR_MESSAGE)) {
      showLoginPage();
    }
  }
});

const getRestLink = (): RestLink => new RestLink({ uri: filevineState.tenantUrl });

const useFilevineClient = (): IFilevineClientHook => {
  const { clientIpAddress } = useIpAddress();
  const { userId, orgId, client } = useProxy(filevineState);
  const { user: oidcUser } = useProxy(oidcUserStore);
  const [isOidcAuthenticated, setIsOidcAuthenticated] = useState(!!oidcUser);
  const history = useHistory();

  const setTenantId = useCallback(() => {
    filevineState.tenantId = oidcUser?.profile?.tenant_id as string || '';
  }, [oidcUser]);

  useEffect(() => {
    if (!oidcUser) {
      loadUser().then((user) => {
        setIsOidcAuthenticated(!!user);
      }).catch(() => {
        setIsOidcAuthenticated(false);
      });
    }
  }, [oidcUser]);

  useEffect(() => {
    setIsOidcAuthenticated(!!oidcUser);
    setTenantId();
  }, [oidcUser, setTenantId]);

  const fetchUserAndOrgId = (accessToken: string) => {
    return fetch(`${filevineState.tenantUrl}/utils/GetUserOrgsWithToken`, {
      method: 'post',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-fv-application': X_FV_APP_HEADER_VALUE,
      },
    }).then((response: Response) => {
      if (response.ok) {
        return response.json();
      }
      throw new Error();
    }).catch(() => {
      throw new Error('There was an error fetching your user and org id from Filevine. Error: FVOA631');
    });
  };

  const formatTenantUrl = (config: IFVAuthTenantConfig): string | undefined => {
    const { hostNames } = config;
    const hostName = hostNames?.find((h) => h.hostNameType === 0)?.hostName;
    return hostName ? `https://${hostName}/v2` : undefined;
  };

  const fetchTenantUrl = useCallback(async (accessToken: string): Promise<void> => {
    try {
      const { fvAuthUrl } = getConfig();
      const response = await fetch(`${fvAuthUrl}/api/tenants/${filevineState.tenantId}`, {
        method: 'get',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error();
      const data: IFVAuthTenantConfig = await response.json();
      filevineState.tenantUrl = formatTenantUrl(data);
    } catch {
      throw new Error('There was an error fetching your tenant details from Filevine.');
    }
  }, []);

  const fetchAndSetUserAndOrgId = useCallback(async (accessToken: string) => {
    const userOrgAndId: IUserAndOrgId = await fetchUserAndOrgId(accessToken);
    if (userOrgAndId?.orgs && userOrgAndId?.user?.userId?.native) {
      if (!userOrgAndId.orgs?.length) {
        throw new Error('This feature only supports active subscribed Filevine users...');
      }

      userOrgAndId.orgs.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
      filevineState.userOrgs = userOrgAndId.orgs;
      filevineState.userId = userOrgAndId.user.userId.native;

      if (!filevineState.orgId || !(userOrgAndId.orgs.findIndex((x) => x.orgId === filevineState.orgId) > -1)) {
        filevineState.orgId = userOrgAndId.orgs[0].orgId;
      }
      return true;
    }
    throw new Error('There was an error setting your Filevine user or org id. Error: FVOA853');
  }, []);

  const updateFVUserFromSession = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        await fetchTenantUrl(accessToken);
        await fetchAndSetUserAndOrgId(accessToken);
      } else {
        throw new Error('Unable to get access token.');
      }
    } catch (error) {
      console.error('Error updating Filevine user from session:', error);
    }
  }, [fetchTenantUrl, fetchAndSetUserAndOrgId]);

  const getAuthLink = useCallback(
    (oId: number, uId: number) => setContext(async (_, { headers, ...rest }) => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Unable to get access token');
        return {
          ...rest,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-fv-application': X_FV_APP_HEADER_VALUE,
            'x-fv-clientIp': clientIpAddress,
            'x-fv-orgId': oId,
            'x-fv-userId': uId,
          },
        };
      } catch (error) {
        console.error('Error getting auth token:', error);
        throw new Error('Unable to create auth link');
      }
    }),
    [clientIpAddress],
  );

  const getClient = useCallback(
    (oId: number, uId: number) => (oId && uId ? new ApolloClient({
      cache: new InMemoryCache(),
      link: ApolloLink.from([getErrorLink(signout), getAuthLink(oId, uId), getRestLink()]),
    }) : undefined),
    [getAuthLink],
  );

  useEffect(() => {
    if (orgId && userId && clientIpAddress) {
      const cli = getClient(orgId, userId);
      filevineState.client = cli;
    }
  }, [clientIpAddress, getClient, orgId, userId]);

  const changeOrg = useCallback((id: number) => {
    filevineState.orgId = id;
  }, []);

  const logout = useCallback((): Promise<void> | undefined => {
    filevineState.client = undefined;
    filevineState.orgId = undefined;
    filevineState.userId = undefined;
    filevineState.userOrgs = [];
    filevineState.tenantId = undefined;
    filevineState.tenantUrl = undefined;
    oidcUserStore.user = null;

    return new Promise<void>((resolve) => {
      try {
        return client?.cache.reset().finally(() => resolve());
      } catch {
        return resolve();
      }
    });
  }, [client]);

  const onFVUserConnectSuccess = useCallback(async () => {
    try {
      await updateFVUserFromSession();
      const paramsString = localStorage.getItem('filevineUrlParams');
      const historyId = localStorage.getItem('historyIdParam');

      if (paramsString) {
        const params: { orgId: number, projectId: number } = JSON.parse(paramsString);
        localStorage.removeItem('filevineUrlParams');
        filevineState.orgId = params.orgId;
        history.push(`/?projectId=${params.projectId}&orgId=${params.orgId}`);
      } else if (historyId) {
        history.push(`/calculator/deadlines/${historyId}`);
        localStorage.removeItem('historyIdParam');
      } else {
        history.push('/calculator/deadlines');
      }

      getSnackBarService().showSnackBar('You successfully connected your account with Filevine.');
    } catch {
      getSnackBarService().showSnackBar('There was an error connecting your account with Filevine.');
      history.push('/calculator');
    }
  }, [updateFVUserFromSession, history]);

  return {
    client: client as FilevineApolloClient,
    logout,
    onFVUserConnectSuccess,
    changeOrg,
    isOidcAuthenticated,
  };
};

export default useFilevineClient;